import { Box, type SxProps, type Theme } from "@mui/material";
import { type CSSProperties, type FC, useEffect, useState } from "react";

interface ThemedSvgProps {
	/** Path to the SVG asset, e.g. "/bonus.svg" or "/charge.svg" — same string you'd use in <img src=>. */
	src: string;
	/** Pixel size for the rendered icon. */
	size?: number | string;
	/** CSS color to apply to all `currentColor` fills/strokes inside the SVG. Pass any var(--inhouse-primary*) for theming. */
	color?: CSSProperties["color"];
	alt?: string;
	sx?: SxProps<Theme>;
	style?: CSSProperties;
	"data-testid"?: string;
}

const inlineCache = new Map<string, Promise<string>>();

/**
 * Replaces every concrete `fill="…"` and `stroke="…"` attribute with
 * `currentColor` so the inlined SVG inherits the parent's CSS color. Skips
 * `fill="none"` / `stroke="none"` (those are layout, not paint) and any
 * gradient/url references like `fill="url(#…)"`.
 */
const SVG_PAINT_RE = /(fill|stroke)\s*=\s*"(?!none\b|url\b|currentColor\b)[^"]+"/gi;
const tintInlineSvg = (svg: string): string => svg.replace(SVG_PAINT_RE, '$1="currentColor"');

/**
 * Defense-in-depth strip of executable content before inlining. Even though
 * sources are constrained to local `/public/*.svg` assets, a future supply-chain
 * slip (or an asset rebuild that pulls in an upstream icon set) shouldn't be
 * able to execute. Drops `<script>` blocks, `on*=` event handlers, and any
 * `href="javascript:…"` payloads.
 */
const SCRIPT_TAG_RE = /<script\b[^>]*>[\s\S]*?<\/script\s*>/gi;
const EVENT_HANDLER_RE = /\s+on[a-z]+\s*=\s*("[^"]*"|'[^']*'|[^\s>]+)/gi;
const JS_HREF_RE = /\s+(href|xlink:href)\s*=\s*("javascript:[^"]*"|'javascript:[^']*')/gi;
const sanitizeInlineSvg = (svg: string): string => svg.replace(SCRIPT_TAG_RE, "").replace(EVENT_HANDLER_RE, "").replace(JS_HREF_RE, "");

/**
 * Renders an SVG asset inline so its `currentColor` paths can be tinted via
 * the parent CSS color. Use as a drop-in replacement for `<img src>` when the
 * icon should follow the user's theme color.
 *
 * SVGs imported as React components via SVGR (e.g. `import Icon from "./x.svg?react"`)
 * are usually preferable; use this helper only for assets in `/public` that
 * are already referenced by string path.
 */
export const ThemedSvg: FC<ThemedSvgProps> = ({ src, size = 20, color = "currentColor", alt, sx, style, "data-testid": dataTestId }) => {
	const [markup, setMarkup] = useState<string>("");

	useEffect(() => {
		const cached = inlineCache.get(src);
		const fetchPromise =
			cached ??
			fetch(src)
				.then((r) => {
					if (!r.ok) throw new Error(`ThemedSvg fetch failed: ${r.status} ${src}`);
					return r.text();
				})
				.then((text) =>
					tintInlineSvg(
						sanitizeInlineSvg(
							text
								// drop XML decl / comments — they trip dangerouslySetInnerHTML
								.replace(/<\?xml[^?]*\?>/g, "")
								.replace(/<!DOCTYPE[^>]*>/g, ""),
						),
					),
				);
		if (!cached) inlineCache.set(src, fetchPromise);
		let cancelled = false;
		fetchPromise
			.then((text) => {
				if (!cancelled) setMarkup(text);
			})
			.catch(() => {
				// Leave markup empty on fetch/parse failure; consumers fall back to alt text.
				inlineCache.delete(src);
			});
		return () => {
			cancelled = true;
		};
	}, [src]);

	return (
		<Box
			role="img"
			aria-label={alt}
			data-testid={dataTestId}
			sx={[
				{
					display: "inline-flex",
					alignItems: "center",
					justifyContent: "center",
					width: size,
					height: size,
					color,
					"& svg": {
						width: "100%",
						height: "100%",
					},
				},
				...(Array.isArray(sx) ? sx : [sx ?? false]),
			]}
			style={style}
			// biome-ignore lint/security/noDangerouslySetInnerHtml: trusted local SVG assets
			dangerouslySetInnerHTML={{ __html: markup }}
		/>
	);
};
